// src/components/home/SponsoredProductsSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  documentId,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  trackSponsoredClick,
  trackSponsoredImpression,
} from "../../lib/sponsoredTracking";

function formatMoney(value) {
  const n = Number(value || 0);
  return `${n.toLocaleString("fr-FR")} FCFA`;
}

function normalizeTimestamp(ts) {
  if (!ts) return null;
  try {
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    const d = new Date(ts);
    const t = d.getTime();
    return Number.isNaN(t) ? null : t;
  } catch {
    return null;
  }
}

function isPlacementActive(placement) {
  if (!placement || placement.isActive !== true) return false;

  const now = Date.now();
  const startAt = normalizeTimestamp(placement.startAt);
  const endAt = normalizeTimestamp(placement.endAt);

  if (startAt && now < startAt) return false;
  if (endAt && now > endAt) return false;

  return true;
}

function chunkArray(arr, size = 10) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

function getProductName(product) {
  return (
    product?.shortName ||
    product?.name ||
    product?.Name ||
    product?.title ||
    product?.Title ||
    product?.productName ||
    product?.ProductName ||
    "Produit sponsorisé"
  );
}

function getProductPrice(product) {
  return (
    product?.salePrice ??
    product?.SalePrice ??
    product?.price ??
    product?.Price ??
    product?.Cass_Stock_Price ??
    0
  );
}

function getProductImage(product) {
  return (
    product?.ImageUrl ||
    product?.imageUrl ||
    product?.image ||
    (Array.isArray(product?.images) && product.images[0]) ||
    ""
  );
}

function getProductLink(product) {
  if (product?.slug && product?.id) return `/ip/${product.slug}/${product.id}`;
  if (product?.id) return `/p/${product.id}`;
  return "#";
}

export default function SponsoredProductsSection({
  title = "Produits sponsorisés",
  subtitle = "Sélection mise en avant par nos boutiques partenaires",
  placementKey = "homepage_products",
  maxPlacements = 8,
}) {
  const [loading, setLoading] = useState(true);
  const [placements, setPlacements] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const trackedImpressionsRef = useRef(new Set());

  useEffect(() => {
    let isMounted = true;

    const loadSponsoredProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const placementSnap = await getDocs(
          query(
            collection(db, "sponsoredPlacements"),
            where("type", "==", "product"),
            where("placement", "==", placementKey),
            where("isActive", "==", true),
            orderBy("priority", "asc"),
            limit(maxPlacements)
          )
        );

        const rawPlacements = placementSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const activePlacements = rawPlacements.filter(isPlacementActive);

        if (!isMounted) return;

        setPlacements(activePlacements);

        const productIds = [];
        activePlacements.forEach((placement) => {
          if (Array.isArray(placement.productIds)) {
            placement.productIds.forEach((id) => {
              if (id && !productIds.includes(id)) {
                productIds.push(id);
              }
            });
          }
        });

        if (productIds.length === 0) {
          setProducts([]);
          setLoading(false);
          return;
        }

        const chunks = chunkArray(productIds, 10);
        const results = [];

        for (const idsChunk of chunks) {
          const productsSnap = await getDocs(
            query(collection(db, "productsShop"), where(documentId(), "in", idsChunk))
          );

          productsSnap.docs.forEach((d) => {
            results.push({
              id: d.id,
              ...d.data(),
            });
          });
        }

        if (!isMounted) return;

        const productMap = new Map(results.map((p) => [p.id, p]));
        const orderedProducts = [];

        activePlacements.forEach((placement) => {
          (placement.productIds || []).forEach((productId) => {
            const product = productMap.get(productId);
            if (product) {
              orderedProducts.push({
                ...product,
                __placementId: placement.id,
                __sourceDealId: placement.sourceDealId || null,
                __placementTitle: placement.title || title,
                __placementSubtitle: placement.subtitle || subtitle,
                __placementPriority: placement.priority ?? 999,
              });
            }
          });
        });

        setProducts(orderedProducts);
      } catch (err) {
        console.error("Erreur chargement produits sponsorisés :", err);
        if (isMounted) {
          setError("Impossible de charger les produits sponsorisés.");
          setProducts([]);
          setPlacements([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSponsoredProducts();

    return () => {
      isMounted = false;
    };
  }, [placementKey, maxPlacements, title, subtitle]);

  useEffect(() => {
    const run = async () => {
      for (const placement of placements) {
        if (!placement?.id) continue;
        if (trackedImpressionsRef.current.has(placement.id)) continue;

        trackedImpressionsRef.current.add(placement.id);

        await trackSponsoredImpression({
          placementId: placement.id,
          sourceDealId: placement.sourceDealId || null,
        });
      }
    };

    if (placements.length) {
      run();
    }
  }, [placements]);

  const visibleProducts = useMemo(() => {
    return products.filter(Boolean);
  }, [products]);

  const handleSponsoredClick = async (product) => {
    await trackSponsoredClick({
      placementId: product.__placementId,
      sourceDealId: product.__sourceDealId,
    });
  };

  if (loading) {
    return (
      <section className="sps-section">
        <div className="sps-header">
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>

        <div className="sps-loading">Chargement des produits sponsorisés...</div>

        <style>{styles}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section className="sps-section">
        <div className="sps-header">
          <div>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
        </div>

        <div className="sps-error">{error}</div>

        <style>{styles}</style>
      </section>
    );
  }

  if (!visibleProducts.length) return null;

  return (
    <section className="sps-section">
      <div className="sps-header">
        <div>
          <div className="sps-kicker">Boost Ventes</div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="sps-pill">
          {placements.length} campagne{placements.length > 1 ? "s" : ""} active
          {placements.length > 1 ? "s" : ""}
        </div>
      </div>

      <div className="sps-grid">
        {visibleProducts.map((product) => {
          const name = getProductName(product);
          const price = getProductPrice(product);
          const image = getProductImage(product);
          const link = getProductLink(product);

          return (
            <article key={`${product.__placementId}-${product.id}`} className="sps-card">
              <div className="sps-badge">Sponsorisé</div>

              <Link
                to={link}
                className="sps-imageWrap"
                onClick={() => handleSponsoredClick(product)}
              >
                {image ? (
                  <img src={image} alt={name} className="sps-image" loading="lazy" />
                ) : (
                  <div className="sps-imageFallback">
                    {String(name || "P").charAt(0).toUpperCase()}
                  </div>
                )}
              </Link>

              <div className="sps-body">
                <div className="sps-top">
                  <div className="sps-vendorWrap">
                    {product.vendorName ? (
                      <div className="sps-vendor">{product.vendorName}</div>
                    ) : (
                      <div className="sps-vendor sps-vendorPlaceholder">Boutique partenaire</div>
                    )}
                  </div>

                  <Link
                    to={link}
                    className="sps-name"
                    onClick={() => handleSponsoredClick(product)}
                    title={name}
                  >
                    {name}
                  </Link>
                </div>

                <div className="sps-bottom">
                  <div className="sps-price">{formatMoney(price)}</div>

                  <div className="sps-actions">
                    <Link
                      to={link}
                      className="sps-btn sps-btn-primary"
                      onClick={() => handleSponsoredClick(product)}
                    >
                      Voir le produit
                    </Link>

                    {product.vendorId ? (
                      <Link
                        to={`/shop/${product.vendorId}`}
                        className="sps-btn sps-btn-light"
                        onClick={() => handleSponsoredClick(product)}
                      >
                        Boutique
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <style>{styles}</style>
    </section>
  );
}

const styles = `
  .sps-section{
    margin:24px 0;
    padding:18px;
    border-radius:24px;
    background:linear-gradient(180deg,#ffffff 0%, #f8fafc 100%);
    border:1px solid #e5e7eb;
    box-shadow:0 16px 36px rgba(15,23,42,.06);
  }

  .sps-header{
    display:flex;
    justify-content:space-between;
    align-items:flex-start;
    gap:16px;
    flex-wrap:wrap;
    margin-bottom:16px;
  }

  .sps-kicker{
    display:inline-flex;
    align-items:center;
    padding:5px 10px;
    border-radius:999px;
    background:#ede9fe;
    color:#5b21b6;
    font-size:11px;
    font-weight:900;
    letter-spacing:.08em;
    text-transform:uppercase;
    margin-bottom:8px;
  }

  .sps-header h2{
    margin:0 0 4px;
    font-size:24px;
    font-weight:900;
    color:#0f172a;
  }

  .sps-header p{
    margin:0;
    font-size:13px;
    color:#64748b;
    max-width:720px;
  }

  .sps-pill{
    display:inline-flex;
    align-items:center;
    padding:8px 12px;
    border-radius:999px;
    background:#eff6ff;
    color:#1d4ed8;
    border:1px solid #dbeafe;
    font-size:12px;
    font-weight:800;
    white-space:nowrap;
  }

  .sps-grid{
    display:grid;
    grid-template-columns:repeat(4, minmax(0,1fr));
    gap:16px;
    align-items:stretch;
  }

  .sps-card{
    position:relative;
    display:flex;
    flex-direction:column;
    height:100%;
    min-height:420px;
    background:#fff;
    border:1px solid #e5e7eb;
    border-radius:20px;
    overflow:hidden;
    box-shadow:0 10px 24px rgba(15,23,42,.05);
    transition:transform .18s ease, box-shadow .18s ease;
  }

  .sps-card:hover{
    transform:translateY(-2px);
    box-shadow:0 16px 32px rgba(15,23,42,.08);
  }

  .sps-badge{
    position:absolute;
    top:12px;
    left:12px;
    z-index:2;
    display:inline-flex;
    align-items:center;
    padding:5px 10px;
    border-radius:999px;
    background:#fff7ed;
    color:#c2410c;
    border:1px solid #fed7aa;
    font-size:11px;
    font-weight:900;
    box-shadow:0 8px 18px rgba(15,23,42,.08);
  }

  .sps-imageWrap{
    display:block;
    width:100%;
    height:220px;
    min-height:220px;
    max-height:220px;
    background:#f8fafc;
    text-decoration:none;
    overflow:hidden;
    flex-shrink:0;
  }

  .sps-image{
    width:100%;
    height:100%;
    object-fit:contain;
    display:block;
  }

  .sps-imageFallback{
    width:100%;
    height:100%;
    display:flex;
    align-items:center;
    justify-content:center;
    font-size:28px;
    font-weight:900;
    color:#94a3b8;
    background:linear-gradient(135deg,#f8fafc,#eef2f7);
  }

  .sps-body{
    padding:14px;
    display:flex;
    flex-direction:column;
    justify-content:space-between;
    flex:1;
    min-height:0;
    gap:12px;
  }

  .sps-top{
    display:flex;
    flex-direction:column;
    gap:8px;
  }

  .sps-bottom{
    display:flex;
    flex-direction:column;
    gap:10px;
    margin-top:auto;
  }

  .sps-vendorWrap{
    min-height:18px;
  }

  .sps-vendor{
    font-size:12px;
    color:#64748b;
    font-weight:700;
    line-height:1.4;
    display:-webkit-box;
    -webkit-line-clamp:1;
    -webkit-box-orient:vertical;
    overflow:hidden;
  }

  .sps-vendorPlaceholder{
    visibility:hidden;
  }

  .sps-name{
    color:#0f172a;
    text-decoration:none;
    font-size:14px;
    font-weight:800;
    line-height:1.35;
    min-height:57px;
    max-height:57px;
    display:-webkit-box;
    -webkit-line-clamp:3;
    -webkit-box-orient:vertical;
    overflow:hidden;
  }

  .sps-name:hover{
    color:#2563eb;
  }

  .sps-price{
    font-size:16px;
    font-weight:900;
    color:#16a34a;
    min-height:24px;
    display:flex;
    align-items:center;
  }

  .sps-actions{
    display:grid;
    grid-template-columns:1fr;
    gap:8px;
    margin-top:2px;
  }

  .sps-btn{
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width:100%;
    min-height:40px;
    padding:8px 12px;
    border-radius:999px;
    text-decoration:none;
    font-size:12px;
    font-weight:800;
    transition:.18s ease;
    text-align:center;
  }

  .sps-btn-primary{
    background:#1d4ed8;
    color:#fff;
    border:1px solid #1d4ed8;
  }

  .sps-btn-primary:hover{
    background:#0f172a;
    border-color:#0f172a;
    color:#fff;
  }

  .sps-btn-light{
    background:#f8fafc;
    color:#111827;
    border:1px solid #e5e7eb;
  }

  .sps-btn-light:hover{
    background:#eef2f7;
    color:#111827;
  }

  .sps-loading,
  .sps-error{
    font-size:13px;
    color:#64748b;
    padding:12px 0;
  }

  .sps-error{
    color:#b91c1c;
  }

  @media (max-width: 1100px){
    .sps-grid{
      grid-template-columns:repeat(3, minmax(0,1fr));
    }

    .sps-card{
      min-height:410px;
    }
  }

  @media (max-width: 820px){
    .sps-grid{
      grid-template-columns:repeat(2, minmax(0,1fr));
    }

    .sps-imageWrap{
      height:210px;
      min-height:210px;
      max-height:210px;
    }
  }

  @media (max-width: 560px){
    .sps-section{
      padding:14px;
      border-radius:20px;
    }

    .sps-header h2{
      font-size:20px;
    }

    .sps-grid{
      grid-template-columns:minmax(0,1fr);
    }

    .sps-card{
      min-height:unset;
    }

    .sps-body{
      padding:12px;
    }

    .sps-imageWrap{
      height:240px;
      min-height:240px;
      max-height:240px;
    }

    .sps-name{
      min-height:auto;
      max-height:none;
    }
  }
`;